import { Link, Outlet, useMatchRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from '../lib/auth.js';
import { CommandPalette } from '../features/search/CommandPalette.js';
import { openCommandPalette } from '../features/search/palette-store.js';
import { OnboardingPage } from '../features/onboarding/OnboardingPage.js';
import { usePublicBrandingQuery } from '../features/branding/api.js';
import { useSidebarCountsQuery } from '../features/me/api.js';
import { useAiQualificationCountsQuery } from '../features/ai-review/api.js';
import { NotificationBell } from '../features/notifications/NotificationBell.js';
import { RealtimeProvider } from '../features/realtime/RealtimeProvider.js';

interface NavItem {
  to: string;
  label: string;
  enabled: boolean;
  icon: (color: string) => ReactNode;
  /** Which real count (from `useSidebarCountsQuery`) to badge this item
   * with, if any — design/orion-crm-design/screenshots/Sidebar.png shows
   * Leads and Tasks each with one. Never a hardcoded number. */
  countKey?: 'leads' | 'tasks';
  /** Leads' count is a plain muted number; Tasks' is an amber pill — exact
   * distinction from source/Sidebar.dc.html. */
  badgeVariant?: 'plain' | 'pill';
}

/** 18×18, stroke 1.8, round caps/joins — exact icon set from
 * design/orion-crm-design/source/Sidebar.dc.html. `color` is passed in
 * rather than baked in, since the same icon renders amber when its item is
 * active and muted grey otherwise (design/orion-crm-design/screenshots/Sidebar.png). */
function navIcon(paths: ReactNode) {
  return (color: string) => (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths}
    </svg>
  );
}

const ICONS = {
  dashboard: navIcon(
    <>
      <rect x="3" y="3" width="7" height="9" rx="1.5" />
      <rect x="14" y="3" width="7" height="5" rx="1.5" />
      <rect x="14" y="12" width="7" height="9" rx="1.5" />
      <rect x="3" y="16" width="7" height="5" rx="1.5" />
    </>,
  ),
  leads: navIcon(
    <>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="5" />
      <circle cx="12" cy="12" r="1" />
    </>,
  ),
  contacts: navIcon(
    <>
      <circle cx="9" cy="8" r="4" />
      <path d="M2 21c0-3.9 3.1-7 7-7s7 3.1 7 7" />
      <path d="M16 4.1a4 4 0 0 1 0 7.8" />
      <path d="M22 21c0-3-1.8-5.6-4.5-6.6" />
    </>,
  ),
  companies: navIcon(
    <>
      <rect x="4" y="3" width="12" height="18" rx="1" />
      <path d="M16 9h4v12h-4" />
      <path d="M8 7h4M8 11h4M8 15h4" />
    </>,
  ),
  prospecting: navIcon(
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </>,
  ),
  deals: navIcon(
    <>
      <rect x="3" y="7" width="18" height="13" rx="2" />
      <path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
      <path d="M3 13h18" />
    </>,
  ),
  tasks: navIcon(
    <>
      <rect x="3" y="3" width="18" height="18" rx="3" />
      <path d="m8 12 3 3 5-6" />
    </>,
  ),
  activities: navIcon(<path d="M3 12h4l3-8 4 16 3-8h4" />),
  calendar: navIcon(
    <>
      <rect x="3" y="4" width="18" height="17" rx="2" />
      <path d="M3 9h18M8 2v4M16 2v4" />
    </>,
  ),
  reports: navIcon(<path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />),
  aiReview: navIcon(
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 3v3M12 18v3M5 12H2M22 12h-3M7 7 5 5M19 19l-2-2M17 7l2-2M5 19l2-2" />
    </>,
  ),
  whatsapp: navIcon(<path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />),
  administration: navIcon(
    <>
      <path d="M4 6h10M18 6h2M4 12h4M12 12h8M4 18h12" />
      <circle cx="16" cy="6" r="2" />
      <circle cx="10" cy="12" r="2" />
      <circle cx="18" cy="18" r="2" />
    </>,
  ),
  help: navIcon(
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9a2.5 2.5 0 1 1 3.5 2.3c-.6.3-1 .9-1 1.6v.6" />
      <path d="M12 17h.01" />
    </>,
  ),
};

// Everything on the design canvas is shown so the navigation shape matches
// the approved sitemap (docs/architecture/11-ui-sitemap.md); an item is
// disabled until its page is actually built, rather than linking to one
// that doesn't exist yet.
const NAV: NavItem[] = [
  { to: '/', label: 'Dashboard', enabled: true, icon: ICONS.dashboard },
  {
    to: '/leads',
    label: 'Leads',
    enabled: true,
    icon: ICONS.leads,
    countKey: 'leads',
    badgeVariant: 'plain',
  },
  { to: '/contacts', label: 'Contacts', enabled: true, icon: ICONS.contacts },
  { to: '/companies', label: 'Companies', enabled: true, icon: ICONS.companies },
  { to: '/prospecting', label: 'Prospecting', enabled: true, icon: ICONS.prospecting },
  { to: '/deals', label: 'Opportunities', enabled: true, icon: ICONS.deals },
  {
    to: '/tasks',
    label: 'Tasks',
    enabled: true,
    icon: ICONS.tasks,
    countKey: 'tasks',
    badgeVariant: 'pill',
  },
  { to: '/activities', label: 'Activities', enabled: true, icon: ICONS.activities },
  { to: '/reports', label: 'Reports', enabled: true, icon: ICONS.reports },
];

/** The persistent shell (docs/architecture/38 "CRM Layout"): sidebar + top
 * bar, navigation filtered by what's actually built so far. */
/** design/orion-crm-design/screenshots/Sidebar.png — active row icon turns
 * amber (`#F2B25C`, the brand accent, same as the logo mark's dots);
 * inactive icons stay muted (`#8A909C`). `Link`'s `activeProps` only
 * changes className, not an SVG `stroke` prop, so active state is resolved
 * with `useMatchRoute` instead and threaded through explicitly. */
function NavLink({
  item,
  active,
  count,
  collapsed,
}: {
  item: NavItem;
  active: boolean;
  count?: number;
  collapsed?: boolean;
}) {
  return (
    <Link
      to={item.to}
      title={collapsed ? item.label : undefined}
      className={
        'flex h-9 items-center gap-2.75 rounded-control px-2.5 text-sm font-medium ' +
        (active ? 'bg-[#262B36] text-white' : 'text-nav-text hover:bg-[#1c212b]')
      }
    >
      {item.icon(active ? '#F2B25C' : '#8A909C')}
      {!collapsed && <span className="flex-1">{item.label}</span>}
      {!collapsed &&
        typeof count === 'number' &&
        (item.badgeVariant === 'pill' ? (
          <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-amber px-1.5 text-xs font-[650] text-night">
            {count}
          </span>
        ) : (
          <span className="text-xs tabular-nums text-nav-text-muted">{count}</span>
        ))}
    </Link>
  );
}

/** `children` overrides the router's `<Outlet/>` when given one — an
 * escape hatch for rendering this shell outside the real route tree.
 * Unused by the real router (which always renders `<Outlet/>` as before);
 * the `?__design=<Screen>` preview harness (design/orion-crm-design/CLAUDE_CODE_HANDOFF.md
 * § Phase 0) ended up not needing it after all, since it reuses the real
 * router at the screen's real URL rather than mounting this shell
 * standalone — kept anyway since it's a harmless, general-purpose option. */
const SIDEBAR_COLLAPSED_KEY = 'orion-sidebar-collapsed';

export function AppShell({ children }: { children?: ReactNode } = {}) {
  const { session, logout, hasPermission } = useAuth();
  const { data: branding } = usePublicBrandingQuery();
  const { data: counts } = useSidebarCountsQuery();
  const canReviewAiLeads = hasPermission('ai.review_queue.view');
  const { data: aiQualificationCounts } = useAiQualificationCountsQuery({ enabled: canReviewAiLeads });
  const aiReviewBadgeCount = aiQualificationCounts ? aiQualificationCounts.pendingNeedsReview + aiQualificationCounts.pendingFailed : undefined;
  const navigate = useNavigate();
  const matchRoute = useMatchRoute();
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
    } catch {
      // Per-device convenience only — a private window or blocked storage
      // just means the sidebar defaults to expanded next time.
    }
  }, [collapsed]);

  const handleLogout = async () => {
    await logout();
    await navigate({ to: '/login' });
  };

  const isActive = (to: string) => Boolean(matchRoute({ to, fuzzy: to !== '/' }));

  return (
    <RealtimeProvider>
      <div className="flex min-h-screen bg-ground">
        <nav
          aria-label="Primary"
          className={
            'flex shrink-0 flex-col gap-0.5 overflow-hidden bg-night pb-3 pl-3 pr-3 pt-3.5 text-white transition-[width] duration-200 ' +
            (collapsed ? 'w-16' : 'w-58')
          }
        >
          <Link
            to="/"
            title={collapsed ? (branding?.productName ?? 'Orion') : undefined}
            className="mb-3.5 flex items-center gap-2.5 rounded-control px-2 py-0 hover:bg-[#1c212b]"
            style={{ height: 44 }}
          >
            <span className="flex size-7.5 shrink-0 items-center justify-center overflow-hidden rounded-[8px] bg-night-surface">
              {branding?.logoDarkUrl || branding?.logoUrl ? (
                <img
                  src={branding.logoDarkUrl ?? branding.logoUrl!}
                  alt={branding.productName}
                  className="size-full object-contain p-0.5"
                />
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
                  <circle cx="6.5" cy="16" r="2" fill="#F2B25C" />
                  <circle cx="12" cy="12" r="2" fill="#F2B25C" />
                  <circle cx="17.5" cy="8" r="2" fill="#F2B25C" />
                  <circle cx="5" cy="5" r="1.1" fill="#8A909C" />
                  <circle cx="19" cy="19" r="1.1" fill="#8A909C" />
                </svg>
              )}
            </span>
            {!collapsed && (
              <span className="flex min-w-0 flex-1 flex-col leading-tight">
                <span className="truncate text-[15px] font-[650] tracking-[-0.01em] text-white">
                  {branding?.productName || 'Orion'}
                </span>
                <span className="truncate text-xs text-nav-text-muted">
                  {branding?.workspaceName || 'Workspace'}
                </span>
              </span>
            )}
          </Link>

          {!collapsed && (
            <div className="px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-nav-text-muted">
              Workspace
            </div>
          )}

          {NAV.map((item) =>
            item.enabled ? (
              <NavLink
                key={item.to}
                item={item}
                active={isActive(item.to)}
                count={item.countKey ? counts?.[item.countKey] : undefined}
                collapsed={collapsed}
              />
            ) : (
              <span
                key={item.to}
                title={collapsed ? item.label : undefined}
                className="flex h-9 cursor-not-allowed items-center justify-between rounded-control px-2.5 text-sm font-medium text-[#5B606B]"
              >
                {!collapsed && item.label}
                {!collapsed && (
                  <span className="rounded-full bg-[#232833] px-1.5 py-0.5 text-[10px] font-semibold">
                    Soon
                  </span>
                )}
              </span>
            ),
          )}

          {canReviewAiLeads && (
            <NavLink
              item={{ to: '/ai-review', label: 'AI Lead Review', enabled: true, icon: ICONS.aiReview, badgeVariant: 'pill' }}
              active={isActive('/ai-review')}
              count={aiReviewBadgeCount}
              collapsed={collapsed}
            />
          )}

          {hasPermission('crm.meetings.view') && (
            <NavLink
              item={{ to: '/calendar', label: 'Calendar', enabled: true, icon: ICONS.calendar }}
              active={isActive('/calendar')}
              collapsed={collapsed}
            />
          )}

          {hasPermission('whatsapp.conversations.view') && (
            <NavLink
              item={{ to: '/whatsapp', label: 'WhatsApp', enabled: true, icon: ICONS.whatsapp }}
              active={isActive('/whatsapp')}
              collapsed={collapsed}
            />
          )}

          {!collapsed && counts && counts.savedViews.length > 0 && (
            <>
              <div className="px-2.5 pb-1.5 pt-4.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-nav-text-muted">
                Saved views
              </div>
              {counts.savedViews.map((view) => (
                <Link
                  key={view.id}
                  to="/leads"
                  search={{ view: view.id }}
                  className="flex h-8 items-center gap-2.75 rounded-control px-2.5 text-[13px] text-nav-text hover:bg-[#1c212b]"
                >
                  <span
                    className="size-2 shrink-0 rounded-[3px]"
                    style={{ backgroundColor: view.color }}
                  />
                  <span className="flex-1 truncate">{view.name}</span>
                  <span className="text-xs text-nav-text-muted">{view.count}</span>
                </Link>
              ))}
            </>
          )}

          <div className="flex-1" />
          <NavLink
            item={{
              to: '/admin',
              label: 'Administration',
              enabled: true,
              icon: ICONS.administration,
            }}
            active={isActive('/admin')}
            collapsed={collapsed}
          />
          <a
            href="mailto:support@oes.com"
            title={collapsed ? 'Help & docs' : undefined}
            className="flex h-9 items-center gap-2.75 rounded-control px-2.5 text-sm font-medium text-nav-text hover:bg-[#1c212b]"
          >
            {ICONS.help('#8A909C')}
            {!collapsed && <span className="flex-1">Help &amp; docs</span>}
          </a>

          <div
            className={
              'mt-2.5 flex items-center gap-2.5 border-t border-nav-line pt-2.5' +
              (collapsed ? ' flex-col' : '')
            }
          >
            <span
              title={collapsed ? session?.user.displayName : undefined}
              className="flex size-8 shrink-0 items-center justify-center rounded-full bg-avatar-indigo text-xs font-[650]"
            >
              {(session?.user.displayName ?? '?').slice(0, 2).toUpperCase()}
            </span>
            {!collapsed && (
              <Link
                to="/account/security"
                className="flex min-w-0 flex-1 flex-col leading-tight hover:opacity-80"
                title="Security settings"
              >
                <span className="truncate text-[13px] font-semibold text-white">
                  {session?.user.displayName}
                </span>
                <span className="truncate text-xs text-nav-text-muted">{session?.user.email}</span>
              </Link>
            )}
            {!collapsed && (
              <button
                type="button"
                onClick={handleLogout}
                className="rounded-control px-2 py-1 text-xs font-semibold text-nav-text hover:bg-[#1c212b]"
              >
                Sign out
              </button>
            )}
            <button
              type="button"
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              onClick={() => setCollapsed((c) => !c)}
              className="flex size-8 shrink-0 items-center justify-center rounded-control text-nav-text-muted hover:bg-[#1c212b] hover:text-white"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <path d="M9 3v18" />
                <path d={collapsed ? 'm14 9 3 3-3 3' : 'm16 15-3-3 3-3'} />
              </svg>
            </button>
          </div>
        </nav>

        <div className="flex min-w-0 flex-1 flex-col">{children ?? <Outlet />}</div>
        <CommandPalette />
        <OnboardingPage />
      </div>
    </RealtimeProvider>
  );
}

const NEW_MENU_ITEMS = [
  { label: 'Lead', to: '/leads/new' },
  { label: 'Contact', to: '/contacts/new' },
  { label: 'Company', to: '/companies/new' },
  { label: 'Opportunity', to: '/deals/new' },
  { label: 'Task', to: '/tasks' },
  { label: 'Log activity', to: '/activities' },
] as const;

/** design/orion-crm-design/screenshots/Topbar.png "New ▾" quick-create
 * menu. Every item routes to a real create screen except Task/Log
 * activity, which don't have their own dedicated create route yet — those
 * open the list page they'd be created from rather than a dead link or an
 * invented one. */
function NewMenu() {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex h-9 items-center gap-1.5 rounded-control bg-primary px-3.5 text-sm font-semibold text-white hover:bg-primary-hover"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#FFFFFF"
          strokeWidth="2.2"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <path d="M12 5v14M5 12h14" />
        </svg>
        New
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#FFFFFF"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-11 z-50 w-44 overflow-hidden rounded-dialog border border-border bg-surface py-1 shadow-dialog">
            {NEW_MENU_ITEMS.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                onClick={() => setOpen(false)}
                className="block px-3.5 py-2 text-sm text-ink hover:bg-surface-subtle"
              >
                {item.label}
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** design/orion-crm-design/screenshots/Topbar.png avatar menu. */
function AvatarMenu() {
  const { session, hasPermission, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    await navigate({ to: '/login' });
  };

  return (
    <div className="relative">
      <button
        type="button"
        aria-label="Account menu"
        onClick={() => setOpen((o) => !o)}
        className="flex size-7.5 items-center justify-center rounded-full bg-avatar-indigo text-xs font-semibold text-white"
      >
        {(session?.user.displayName ?? '?').slice(0, 2).toUpperCase()}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-11 z-50 w-52 overflow-hidden rounded-dialog border border-border bg-surface py-1 shadow-dialog">
            <div className="border-b border-divider px-3.5 py-2.5">
              <p className="truncate text-[13px] font-semibold text-ink">
                {session?.user.displayName}
              </p>
              <p className="truncate text-xs text-text-muted">{session?.user.email}</p>
            </div>
            <Link
              to="/account/security"
              onClick={() => setOpen(false)}
              className="block px-3.5 py-2 text-sm text-ink hover:bg-surface-subtle"
            >
              Security settings
            </Link>
            <Link
              to="/account/mailbox"
              onClick={() => setOpen(false)}
              className="block px-3.5 py-2 text-sm text-ink hover:bg-surface-subtle"
            >
              Mailbox
            </Link>
            {hasPermission('admin.users.view') && (
              <Link
                to="/admin"
                onClick={() => setOpen(false)}
                className="block px-3.5 py-2 text-sm text-ink hover:bg-surface-subtle"
              >
                Administration
              </Link>
            )}
            <button
              type="button"
              onClick={handleLogout}
              className="block w-full px-3.5 py-2 text-left text-sm text-danger hover:bg-danger-soft"
            >
              Sign out
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/** design/orion-crm-design/screenshots/Topbar.png, folded into the
 * existing per-page `PageHeader` rather than a separate always-mounted
 * component: `title` doubles as the breadcrumb, `actions` still carries
 * whatever page-specific buttons already existed (Export CSV, Create
 * form, ...), rendered just left of the constant New/notifications/avatar
 * cluster. Doing it this way means every one of the ~30 existing call
 * sites gets the Topbar upgrade for free, with zero risk of breaking their
 * own actions. */
export interface Breadcrumb {
  label: string;
  to?: string;
}

/** design/orion-crm-design/rendered-html/LeadsList.html's Topbar shows a
 * 2-level trail ("Leads › All leads") on list screens, not just a single
 * title — `breadcrumb` renders that exactly (muted 500-weight parent
 * segments, ink 600-weight current segment); `title` stays as the simple
 * single-segment fallback the other ~40 existing call sites use. */
export function PageHeader({
  title,
  breadcrumb,
  actions,
}: {
  title?: string;
  breadcrumb?: Breadcrumb[];
  actions?: ReactNode;
}) {
  return (
    <header className="flex h-15 shrink-0 items-center gap-4 border-b border-border bg-ground pl-6 pr-5">
      <nav aria-label="Breadcrumb" className="flex min-w-65 shrink-0 items-center gap-2 text-sm">
        {breadcrumb ? (
          breadcrumb.map((crumb, i) => (
            <span key={crumb.label} className="flex items-center gap-2">
              {i > 0 && (
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#9A9FA8"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="m9 6 6 6-6 6" />
                </svg>
              )}
              {crumb.to ? (
                <Link
                  to={crumb.to}
                  className={
                    i === breadcrumb.length - 1
                      ? 'font-semibold text-ink'
                      : 'font-medium text-text-muted hover:text-ink'
                  }
                >
                  {crumb.label}
                </Link>
              ) : (
                <span
                  className={
                    i === breadcrumb.length - 1
                      ? 'font-semibold text-ink'
                      : 'font-medium text-text-muted'
                  }
                >
                  {crumb.label}
                </span>
              )}
            </span>
          ))
        ) : (
          <h1 className="text-sm font-semibold text-ink">{title}</h1>
        )}
      </nav>

      <div className="flex flex-1 justify-center">
        <button
          type="button"
          onClick={() => openCommandPalette()}
          className="flex h-9 w-full max-w-110 items-center gap-2.5 rounded-input border border-border bg-surface pl-3 pr-2 text-left text-sm text-text-muted hover:bg-surface-subtle"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#5E6470"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" />
          </svg>
          <span className="flex-1">Search leads, contacts, companies, opportunities…</span>
          <kbd className="rounded-sm border border-border bg-ground px-1.5 py-0.5 font-mono text-[11px] text-text-muted">
            Ctrl K
          </kbd>
        </button>
      </div>

      <div className="flex shrink-0 items-center gap-1.5">
        {actions}
        <NewMenu />
        <NotificationBell />
        <AvatarMenu />
      </div>
    </header>
  );
}
